<template>
  <main class="auction-area-card">
    <div class="arena-title">
      <a-icon type="thunderbolt" />
      竞拍战场
      <div class="arena-subtitle">Battle Arena</div>
    </div>

    <div class="auction-area">
      <!-- 欢迎界面 -->
      <div v-if="venueInfo.status === 1" class="welcome-screen">
        <div class="welcome-content">
          <div class="welcome-title">
            <a-icon type="thunderbolt" />
            {{ venueInfo.name }}
          </div>
          <div class="welcome-stats">
            <div class="stat-item">
              <div class="stat-number">{{ requirementPool.length }}</div>
              <div class="stat-label">待竞拍需求</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ teamList.length }}</div>
              <div class="stat-label">参战团队</div>
            </div>
          </div>
          <div class="welcome-countdown">
            <a-statistic-countdown :value="venueDeadline" format="m 分 s 秒" @finish="$emit('change')" />
          </div>
        </div>
      </div>

      <!-- 当前竞拍需求 -->
      <div v-if="venueInfo.status === 2 && currentProduct" class="current-requirement">
        <div class="requirement-spotlight">
          <div class="spotlight-content">
            <div class="current-requirement-tag">
              <a-icon type="target" />
              BR-{{ currentProduct.id }}
            </div>
            <div class="current-requirement-title">
              {{ currentProduct.title || '需求标题' }}
            </div>
            <div class="requirement-progress">第 {{ currentProductIndex + 1 }} / {{ requirementPool.length }} 个需求</div>
          </div>
        </div>
      </div>

      <!-- 竞拍状态 -->
      <div v-if="venueInfo.status === 2" class="auction-status">
        <div v-if="auctionState === 'waiting'" class="status-text waiting"><a-icon type="loading" spin /> 等待 {{ currentTeam?.participant_name || '团队' }} 做出选择...</div>
        <div v-else-if="auctionState === 'finished'" class="status-text finished"><a-icon type="trophy" /> 竞拍大会圆满结束！</div>
        <div v-else-if="auctionState === 'no-requirements'" class="status-text empty"><a-icon type="inbox" /> 暂无待竞拍需求</div>
      </div>

      <!-- 倒计时 -->
      <div v-if="venueInfo.status === 2" class="countdown">
        <div class="countdown-ring">
          <div class="countdown-text">思考时间</div>
          <a-statistic-countdown :value="deadline" format="m 分 s 秒" @finish="$emit('change')" />
          <div class="countdown-tip">
            <span>请谨慎考虑，做出明智决策</span>
          </div>
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div v-if="venueInfo.status === 2 && pendingRecords.length > 0" class="action-buttons">
        <template v-if="pendingRecords[0].result === 1">
          <a-button type="primary" size="large" :loading="actionLoading" class="accept-btn" icon="check" @click="venueActionHandle(pendingRecords[0], 'ACCEPT')"> 接受挑战 </a-button>
          <a-button type="danger" size="large" :loading="actionLoading" class="reject-btn" icon="close" @click="venueActionHandle(pendingRecords[0], 'REJECT')"> 放弃挑战 </a-button>
        </template>
        <template v-if="pendingRecords[0].result === 2">
          <a-button type="primary" size="large" :loading="actionLoading" class="accept-btn" icon="check" @click="venueActionHandle(pendingRecords[0], 'PRE_ACCEPT')"> 预接受挑战 </a-button>
          <a-button type="danger" size="large" :loading="actionLoading" class="reject-btn" icon="close" @click="venueActionHandle(pendingRecords[0], 'PRE_REJECT')"> 预放弃挑战 </a-button>
        </template>
      </div>

      <!-- 团队选择区域 -->
      <team-selection v-if="[1, 2].includes(venueInfo.status)" :display-teams="displayTeams" :auction-started="auctionStarted" @team-action="handleTeamAction" />

      <!-- 竞拍结果显示 -->
      <div v-if="venueInfo.status === 3" class="auction-results">
        <a-result status="success" title="竞拍已结束" sub-title="竞拍大会圆满结束！期待您的下次参与~" />
        <div v-if="false" class="results-header">
          <div class="results-title">
            <a-icon type="trophy" />
            竞拍结果
          </div>
          <div class="results-summary">{{ initialRequirements.filter(item => item.status === 3).length }}个需求完成竞拍</div>
        </div>

        <!-- 竞拍统计概览 -->
        <div class="auction-overview">
          <div class="overview-title">
            <a-icon type="bar-chart" />
            竞拍统计概览
          </div>
          <div class="overview-stats">
            <div class="stat-card">
              <div class="stat-icon total">
                <a-icon type="file-text" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ auctionStats.total }}</div>
                <div class="stat-label">总需求数</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon success">
                <a-icon type="check-circle" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ auctionStats.successful }}</div>
                <div class="stat-label">成功竞拍</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon failed">
                <a-icon type="close-circle" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ auctionStats.failed }}</div>
                <div class="stat-label">流拍需求</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon rate">
                <a-icon type="percentage" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ auctionStats.successRate }}%</div>
                <div class="stat-label">成功率</div>
              </div>
            </div>
          </div>
        </div>
        <div class="auction-overview">
          <div class="overview-title">
            <a-icon type="bars" />
            竞拍结果明细
          </div>
        </div>
        <div class="results-list">
          <div v-for="result in initialRequirements" :key="result.id" class="result-item">
            <div class="requirement-id">
              <span class="requirement-tag">BR-{{ result.source_id }}</span>
            </div>
            <a class="requirement-title" :href="requirementLink(result)" target="_blank">{{ result.source_name || '需求标题' }}</a>
            <div class="assignment-result">
              <template v-if="result.winner !== null">
                <span class="success-result">
                  <a-icon type="team" />
                  {{ result.winner.name }}
                </span>
              </template>
              <template v-else>
                <span class="failed-result">
                  <a-icon type="minus-circle" />
                  流拍
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- 控制按钮 -->
      <div class="control-buttons">
        <a-space>
          <a-button v-if="venueInfo.status === 1" v-has="{ code: 'business:venue:edit' }" :loading="startLoading" type="primary" size="large" :disabled="requirementPool.length === 0 || teamList.length === 0" class="start-btn" icon="play-circle" @click="startAuction"> 开始竞拍大会 </a-button>
          <template v-if="false">
            <a-button v-if="auctionStarted && auctionState !== 'finished'" type="danger" size="large" class="stop-btn" @click="stopAuction"> <a-icon type="stop" /> 紧急停止 </a-button>
            <a-button v-if="auctionState === 'finished'" type="primary" size="large" class="reset-btn" @click="resetAuction"> <a-icon type="reload" /> 重新开始 </a-button>
          </template>
          <a-button v-if="notReady.length > 0" :loading="readyLoading" type="primary" size="large" class="start-btn" @click="onReady"> 准备好了 </a-button>
        </a-space>
      </div>
    </div>
  </main>
</template>

<script>
import { venueStatusUpdate, venueAction } from '@/api/business/venue';

import dayjs from 'dayjs';
import TeamSelection from '@/pages/business/venue/components/TeamSelection.vue';

export default {
  name: 'AuctionArena',
  components: {
    TeamSelection
  },
  props: {
    venueInfo: {
      type: Object,
      default: () => {
        return {};
      }
    },
    // 简化props - 只接收初始数据
    initialRequirements: {
      type: Array,
      default: () => []
    },
    initialTeams: {
      type: Array,
      default: () => []
    },
    pendingRecords: {
      type: Array,
      default: () => {
        return [];
      }
    }
  },
  emits: [
    'requirement-status-change', // 通知需求状态变化
    'auction-finished' // 通知竞拍完成
  ],
  data() {
    return {
      // 竞拍核心状态 - 由AuctionArena管理
      requirementPool: [],
      teamList: [],
      teamQueue: [],
      teamParticipation: {},

      auctionStarted: false,
      auctionState: 'waiting', // waiting, finished, no-requirements
      currentProductIndex: 0,

      // 结果
      auctionResults: [],

      // UI状态
      actionLoading: false,
      startLoading: false,
      readyLoading: false
    };
  },
  computed: {
    currentProduct() {
      return this.requirementPool.find(item => item.status === 2) || null;
    },
    currentTeam() {
      return this.currentProduct?.records.find(item => item.result === 1) || null;
    },
    deadline() {
      if (!this.currentTeam || !this.currentTeam.deadline) return Date.now();
      return dayjs(this.currentTeam.deadline).valueOf();
    },
    venueDeadline() {
      return dayjs(this.venueInfo.start_time).valueOf();
    },
    displayTeams() {
      if (this.venueInfo.status === 1) {
        return this.teamList;
      }
      console.log(this.currentProduct.records);
      return this.currentProduct.records.map(item => ({
        id: item.participant_id,
        name: item.participant_name,
        result: item.result,
        ready: item.ready
      }));
    },
    // 竞拍统计数据
    auctionStats() {
      const totalRequirements = this.initialRequirements.length;
      const successfulRequirements = this.initialRequirements.filter(item => item.status === 3);
      const failedRequirements = this.initialRequirements.filter(item => item.status === 4);

      return {
        total: totalRequirements,
        successful: successfulRequirements.length,
        failed: failedRequirements.length,
        successRate: totalRequirements > 0 ? Math.round((successfulRequirements.length / totalRequirements) * 100) : 0
      };
    },
    requirementLink() {
      return record => {
        return this.$router.resolve({
          name: 'page:business:requirement:detail',
          params: {
            id: record.source_id
          }
        }).href;
      };
    },
    notReady() {
      const userId = this.$store.state.userInfo.id;
      return this.initialTeams.filter(team => team.ready === false && team.ownerId === userId);
    }
  },
  watch: {
    // 监听初始数据变化
    initialRequirements: {
      handler(newRequirements) {
        this.requirementPool = [...newRequirements];
      },
      immediate: true,
      deep: true
    },
    initialTeams: {
      handler(newTeams) {
        this.teamList = [...newTeams];
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    // ==================== 竞拍控制逻辑 ====================
    startAuction() {
      if (this.requirementPool.length === 0) {
        this.$message.warning('暂无需求可以竞拍');
        return;
      }
      if (this.teamList.length === 0) {
        this.$message.warning('暂无团队参与竞拍');
        return;
      }

      this.startLoading = true;
      venueStatusUpdate({
        id: this.venueInfo.id,
        status: 2
      })
        .then(() => {
          this.auctionStarted = true;
          this.auctionState = 'waiting';
          this.currentProductIndex = 0;
          this.teamQueue = [...this.teamList];
          this.auctionResults = [];
          this.teamParticipation = {};

          this.$emit('change');
          this.$message.success('竞拍大会正式开始！');

          // 通知需求状态变化
          this.emitRequirementStatusChange();
        })
        .finally(() => {
          this.startLoading = false;
        });
    },

    stopAuction() {
      this.auctionStarted = false;
      this.auctionState = 'finished';
      this.$message.info('竞拍已结束');

      this.$emit('auction-finished', this.auctionResults);
    },

    resetAuction() {
      this.auctionStarted = false;
      this.auctionState = 'waiting';
      this.currentProductIndex = 0;
      this.teamQueue = [];
      this.auctionResults = [];
      this.teamParticipation = {};

      // 重置需求状态
      this.requirementPool.forEach(requirement => {
        requirement.status = 'pending';
        requirement.assignedTeam = null;
      });

      this.emitRequirementStatusChange();
    },

    // ==================== 竞拍操作逻辑 ====================
    /**
     * 竞拍
     * @param action
     */
    venueActionHandle(record, action) {
      this.actionLoading = true;
      if (record === null) {
        return;
      }
      venueAction({
        venue_id: this.venueInfo.id,
        record_id: record.id,
        action: action
      })
        .then(() => {
          this.$emit('change');
          switch (action) {
            case 'ACCEPT':
              this.$message.success(`${record.participant_name} 成功接受需求 BR-${this.currentProduct.source_id}`);
              break;
            case 'REJECT':
              this.$message.success(`${record.participant_name} 成功放弃需求 BR-${this.currentProduct.source_id}`);
              break;
            case 'PRE_ACCEPT':
              this.$message.success(`${record.participant_name} 成功预接受需求 BR-${this.currentProduct.source_id}`);
              break;
            case 'PRE_REJECT':
              this.$message.success(`${record.participant_name} 成功预放弃需求 BR-${this.currentProduct.source_id}`);
              break;
          }
        })
        .catch(err => {
          console.error('操作失败', err);
        })
        .finally(() => {
          this.actionLoading = false;
        });
    },

    // ==================== 团队管理逻辑 ====================
    hasTeamParticipated(teamId) {
      if (!this.currentProduct) return false;
      const participants = this.teamParticipation[this.currentProduct.id] || [];
      return participants.includes(teamId);
    },

    recordTeamParticipation(teamId) {
      if (!this.currentProduct) return;
      const requirementId = this.currentProduct.id;
      if (!this.teamParticipation[requirementId]) {
        this.teamParticipation[requirementId] = [];
      }
      if (!this.teamParticipation[requirementId].includes(teamId)) {
        this.teamParticipation[requirementId].push(teamId);
      }
    },

    moveCurrentTeamToEnd() {
      const currentTeam = this.teamQueue.shift();
      this.teamQueue.push(currentTeam);

      if (this.hasAllTeamsParticipated()) {
        this.handleFailedAuction();
      } else {
        this.findNextValidTeam();
      }

      this.actionLoading = false;
    },

    skipCurrentTeam() {
      const currentTeam = this.teamQueue.shift();
      this.teamQueue.push(currentTeam);

      if (this.hasAllTeamsParticipated()) {
        this.handleFailedAuction();
      } else {
        this.findNextValidTeam();
      }
    },

    findNextValidTeam() {
      let attempts = 0;
      while (attempts < this.teamQueue.length && this.hasTeamParticipated(this.teamQueue[0].id)) {
        const team = this.teamQueue.shift();
        this.teamQueue.push(team);
        attempts++;
      }

      if (attempts >= this.teamQueue.length) {
        this.handleFailedAuction();
      }
    },

    hasAllTeamsParticipated() {
      if (!this.currentProduct) return true;
      const participants = this.teamParticipation[this.currentProduct.id] || [];
      return participants.length >= this.teamList.length;
    },

    // ==================== 需求流转逻辑 ====================
    nextRequirement() {
      this.actionLoading = false;
      this.currentProductIndex++;

      if (this.currentProductIndex >= this.requirementPool.length) {
        this.auctionState = 'finished';
        this.$message.success('🎊 所有需求竞拍完成！本次竞拍大会圆满结束！');
        this.$emit('auction-finished', this.auctionResults);
      } else {
        this.requirementPool[this.currentProductIndex].status = 'bidding';
        this.$message.info(`开始竞拍需求 BR-${this.currentProduct.id}，当前轮到 ${this.currentTeam?.name || '团队'}`);
      }

      this.emitRequirementStatusChange();
    },

    handleFailedAuction() {
      this.actionLoading = false;

      this.currentProduct.status = 'failed';
      this.auctionResults.push({
        requirementId: this.currentProduct.id,
        requirementTitle: this.currentProduct.title,
        assignedTeam: null
      });

      this.$message.warning(`需求 BR-${this.currentProduct.id} 流拍！无人接受挑战！`);
      this.nextRequirement();
    },

    // ==================== 事件处理 ====================
    handleTeamAction(team, index) {
      if (index === 0 && this.auctionState === 'waiting') {
        // 当前团队的额外交互逻辑
      }
    },

    // 通知需求状态变化
    emitRequirementStatusChange() {
      this.$emit('requirement-status-change', {
        requirements: this.requirementPool,
        currentIndex: this.currentProductIndex
      });
    },

    onReady() {
      this.readyLoading = true;
      venueAction({
        venue_id: this.venueInfo.id,
        action: 'READY',
        record_id: 0
      })
        .then(() => {
          this.$emit('change');
        })
        .catch(err => {
          this.$message.error(err);
        })
        .finally(() => {
          this.readyLoading = false;
        });
    }
  }
};
</script>

<style lang="less" scoped>
// 样式保持与原来相同...
.auction-area-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: none;
  border-radius: 12px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
}

.arena-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  margin: 0;

  .anticon {
    color: #165dff;
    font-size: 18px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover .anticon {
    transform: scale(1.1);
  }

  .arena-subtitle {
    font-size: 12px;
    opacity: 0.6;
    margin-left: 8px;
    color: #86909c;
  }
}

.auction-area {
  text-align: center;
  padding: 16px;
  flex: 1;
  overflow-y: auto;
}

// 欢迎界面样式
.welcome-screen {
  margin-bottom: 1rem;

  .welcome-content {
    text-align: center;
    padding: 1rem 1rem;

    .welcome-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1d2129;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;

      .anticon {
        color: #165dff;
        font-size: 2rem;
      }
    }

    .welcome-countdown {
      margin: 1rem 0;
    }

    .welcome-subtitle {
      font-size: 1rem;
      color: #86909c;
      margin-bottom: 1rem;
    }

    .welcome-stats {
      display: flex;
      justify-content: center;
      gap: 1rem;

      .stat-item {
        text-align: center;
        background: #ffffff;
        border: 1px solid #f2f3f5;
        border-radius: 8px;
        padding: 1rem;
        min-width: 80px;

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #165dff;
          line-height: 1;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #86909c;
          font-weight: 500;
        }
      }
    }
  }
}

.current-requirement {
  margin-bottom: 1rem;

  .requirement-spotlight {
    position: relative;
    background: linear-gradient(135deg, #1d2129, #202637);
    color: white;
    border-radius: 12px;
    padding: 1rem;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: radial-gradient(circle at 25% 25%, rgba(22, 93, 255, 0.15) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(114, 46, 209, 0.15) 0%, transparent 50%);
      opacity: 0.7;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover::before {
      opacity: 1;
    }

    .spotlight-content {
      position: relative;
      z-index: 2;

      .current-requirement-tag {
        font-size: 1.75rem;
        font-weight: 700;
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        letter-spacing: -0.5px;
      }

      .current-requirement-title {
        font-size: 1rem;
        opacity: 0.9;
        margin-bottom: 0.5rem;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      }

      .requirement-progress {
        font-size: 0.875rem;
        opacity: 0.8;
        font-weight: 500;
      }
    }
  }
}

.auction-status {
  margin-bottom: 1rem;

  .status-text {
    font-size: 1rem;
    font-weight: 600;
    padding: 0.5rem 1.25rem;
    border-radius: 999px;
    display: inline-block;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &.waiting {
      background-color: rgba(255, 125, 0, 0.1);
      color: #ff7d00;
    }

    &.finished {
      background: linear-gradient(90deg, #00b42a, #00a854);
      color: white;
    }

    &.empty {
      background: linear-gradient(90deg, #86909c, #4e5969);
      color: white;
    }
  }
}

.countdown {
  margin-bottom: 1rem;

  .countdown-ring {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 1.25rem;
    background-color: #f7f8fa;
    border-radius: 8px;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(22, 93, 255, 0.2), transparent);
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(22, 93, 255, 0.2), transparent);
    }

    .countdown-text {
      font-size: 0.875rem;
      color: #1d2129;
      font-weight: 600;
    }

    .countdown-display {
      font-size: 3rem;
      font-weight: 700;
      color: #1d2129;
      letter-spacing: -2px;
      margin-bottom: 0.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      font-variant-numeric: tabular-nums;

      &.urgent {
        color: #f53f3f;
      }

      .countdown-number {
        width: 75px;
        height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgba(0, 0, 0, 0.05);
        }

        &::before {
          content: attr(data-label);
          position: absolute;
          bottom: 10px;
          font-size: 0.7rem;
          font-weight: 500;
          color: #86909c;
          letter-spacing: 0;
        }
      }

      .countdown-colon {
        font-size: 2.5rem;
        color: #165dff;
        animation: pulse 1s infinite;
        position: relative;
        top: -5px;
      }
    }

    .countdown-tip {
      font-size: 0.875rem;
      color: #86909c;
      letter-spacing: 0.5px;
      margin-top: 0.25rem;
    }
  }
}

.action-buttons {
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  gap: 1.25rem;

  .accept-btn,
  .reject-btn {
    border: none;
    border-radius: 8px;
    padding: 0.875rem 2.25rem;
    font-size: 1rem;
    font-weight: 600;
    min-width: 120px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover::before {
      left: 100%;
    }

    &:hover {
      transform: translateY(-2px);
    }

    .anticon {
      font-size: 1rem;
    }
  }

  .accept-btn {
    background: linear-gradient(90deg, #00b42a, #00a854);
    color: white;
    box-shadow: 0 3px 10px rgba(0, 180, 42, 0.25);

    &:hover {
      box-shadow: 0 5px 14px rgba(0, 180, 42, 0.35);
    }
  }

  .reject-btn {
    background: linear-gradient(90deg, #f53f3f, #f76560);
    color: white;
    box-shadow: 0 3px 10px rgba(245, 63, 63, 0.25);

    &:hover {
      box-shadow: 0 5px 14px rgba(245, 63, 63, 0.35);
    }
  }
}

.auction-results {
  margin-bottom: 1rem;

  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #f7f8fa, #ffffff);
    border-radius: 8px;
    border: 1px solid #f2f3f5;

    .results-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
      font-weight: 600;
      color: #1d2129;

      .anticon {
        color: #165dff;
        font-size: 1.125rem;
      }
    }

    .results-summary {
      font-size: 0.8125rem;
      color: #86909c;
      background: #f2f3f5;
      padding: 0.25rem 0.75rem;
      border-radius: 999px;
    }
  }

  .auction-overview {
    margin-bottom: 1rem;

    .overview-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;
      font-weight: 600;
      color: #1d2129;
      margin-bottom: 1rem;
      padding-left: 0.25rem;

      .anticon {
        color: #165dff;
        font-size: 1rem;
      }
    }

    .overview-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 1rem;
    }

    .stat-card {
      background: #ffffff;
      border: 1px solid #f2f3f5;
      border-radius: 8px;
      padding: 1rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: transparent;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      &:hover {
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
        transform: translateY(-2px);
        border-color: rgba(22, 93, 255, 0.15);

        &::before {
          background: linear-gradient(180deg, #165dff, #722ed1);
        }
      }

      .stat-icon {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        .anticon {
          font-size: 1.125rem;
          color: white;
        }

        &.total {
          background: linear-gradient(135deg, #165dff, #4080ff);
        }

        &.success {
          background: linear-gradient(135deg, #00b42a, #00d62a);
        }

        &.failed {
          background: linear-gradient(135deg, #f53f3f, #ff6b6b);
        }

        &.rate {
          background: linear-gradient(135deg, #722ed1, #9551f9);
        }
      }

      .stat-content {
        flex: 1;
        min-width: 0;

        .stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1d2129;
          line-height: 1.2;
          margin-bottom: 0.125rem;
          font-variant-numeric: tabular-nums;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #86909c;
          font-weight: 500;
          letter-spacing: 0.3px;
        }
      }
    }
  }

  .results-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .result-item {
    background: #ffffff;
    border: 1px solid #f2f3f5;
    border-radius: 6px;
    padding: 0.75rem 1rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 1rem;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: transparent;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transform: translateY(-1px);
      border-color: rgba(22, 93, 255, 0.1);
    }

    &:hover::before {
      background: linear-gradient(180deg, #165dff, #722ed1);
    }

    .requirement-id {
      flex-shrink: 0;

      .requirement-tag {
        font-size: 0.7rem;
        font-weight: 600;
        background: #165dff;
        color: white;
        padding: 0.15rem 0.5rem;
        border-radius: 999px;
        letter-spacing: 0.2px;
      }
    }

    .requirement-title {
      flex: 1;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1d2129;
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
    }

    .assignment-result {
      flex-shrink: 0;
      min-width: 120px;
      text-align: right;

      .success-result {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: #00b42a;
        background: rgba(0, 180, 42, 0.1);
        padding: 0.2rem 0.6rem;
        border-radius: 999px;

        .anticon {
          color: #00b42a;
          font-size: 0.75rem;
        }
      }

      .failed-result {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: #f53f3f;
        background: rgba(245, 63, 63, 0.1);
        padding: 0.2rem 0.6rem;
        border-radius: 999px;

        .anticon {
          color: #f53f3f;
          font-size: 0.75rem;
        }
      }
    }
  }
}

.control-buttons {
  display: flex;
  justify-content: center;
  gap: 1.25rem;

  .start-btn,
  .stop-btn,
  .reset-btn {
    border: none;
    border-radius: 8px;
    padding: 0.875rem 2.25rem;
    font-size: 1rem;
    font-weight: 600;
    min-width: 120px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover::before {
      left: 100%;
    }

    &:hover {
      transform: translateY(-2px);
    }

    .anticon {
      font-size: 1rem;
    }
  }

  .start-btn {
    background: linear-gradient(90deg, #165dff, #4080ff);
    color: white;
    box-shadow: 0 3px 10px rgba(22, 93, 255, 0.25);

    &:hover:not(:disabled) {
      box-shadow: 0 5px 14px rgba(22, 93, 255, 0.35);
    }

    &:disabled {
      background: #f2f3f5;
      color: #86909c;
      cursor: not-allowed;
      box-shadow: none;

      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  }

  .stop-btn {
    background: linear-gradient(90deg, #f53f3f, #f76560);
    color: white;
    box-shadow: 0 3px 10px rgba(245, 63, 63, 0.25);

    &:hover {
      box-shadow: 0 5px 14px rgba(245, 63, 63, 0.35);
    }
  }

  .reset-btn {
    background: linear-gradient(90deg, #722ed1, #9351f9);
    color: white;
    box-shadow: 0 3px 10px rgba(114, 46, 209, 0.25);

    &:hover {
      box-shadow: 0 5px 14px rgba(114, 46, 209, 0.35);
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

:deep(.ant-result) {
  padding: 16px;
  .ant-result-icon {
    margin-bottom: 12px;
  }
}
</style>
